import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { isAllowedAdminEmail } from '@/lib/adminConfig';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isDealer: boolean;
   isEmailVerified: boolean;
  isLoading: boolean;
  isRoleLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Cache for role check results to prevent duplicate fetches
const roleCheckCache = new Map<string, { isAdmin: boolean; isDealer: boolean; timestamp: number }>();
const ROLE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Deduplication: track in-flight role check promises
const inFlightRoleChecks = new Map<string, Promise<{ isAdmin: boolean; isDealer: boolean }>>();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDealer, setIsDealer] = useState(false);
   const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRoleLoading, setIsRoleLoading] = useState(true);

  const initialSessionResolvedRef = useRef(false);

  // Bootstrap admin role - called once per session
  /**
   * Bootstrap admin role for allowed admin emails.
   * Uses same-origin proxy service for policy compliance.
   */
  const ensureAdminRole = async (sessionUser: User) => {
    const email = sessionUser.email ?? null;
    if (!isAllowedAdminEmail(email)) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) return;

      // Import dynamically to avoid circular deps
      const { bootstrapAdminRole } = await import('@/services/edgeFunctions');
      const { error } = await bootstrapAdminRole(accessToken);

      if (error) {
        console.warn('bootstrap-admin-role failed:', error);
      }
    } catch (err) {
      console.warn('bootstrap-admin-role failed:', err);
    }
  };

  /**
   * OPTIMIZED: Single combined role check with caching and deduplication
   * Prevents the 3-4 duplicate requests seen in network logs
   */
  const fetchRoles = async (userId: string, email: string | null): Promise<{ isAdmin: boolean; isDealer: boolean }> => {
    // Check cache first
    const cached = roleCheckCache.get(userId);
    if (cached && Date.now() - cached.timestamp < ROLE_CACHE_TTL) {
      return { isAdmin: cached.isAdmin, isDealer: cached.isDealer };
    }

    // Check if there's already an in-flight request for this user
    const inFlight = inFlightRoleChecks.get(userId);
    if (inFlight) {
      return inFlight;
    }

    // Create the role check promise
    const roleCheckPromise = (async () => {
      const canBeAdmin = isAllowedAdminEmail(email);

      const adminResult = canBeAdmin
        ? await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', userId)
            .eq('role', 'admin')
            .maybeSingle()
        : { data: null, error: null };

      const adminRole = canBeAdmin && !!adminResult.data;
      const dealerRole = false;

      // Cache the result
      roleCheckCache.set(userId, { isAdmin: adminRole, isDealer: dealerRole, timestamp: Date.now() });

      return { isAdmin: adminRole, isDealer: dealerRole };
    })();

    // Store the in-flight promise
    inFlightRoleChecks.set(userId, roleCheckPromise);

    try {
      const result = await roleCheckPromise;
      return result;
    } finally {
      // Clean up in-flight tracker
      inFlightRoleChecks.delete(userId);
    }
  };

  // Combined role check function that properly tracks loading state
  const performRoleChecks = async (sessionUser: User) => {
    setIsRoleLoading(true);
    const email = sessionUser.email ?? null;
    const shouldBeAdmin = isAllowedAdminEmail(email);

    try {
      // Fetch all roles in one call
      let roles = await fetchRoles(sessionUser.id, email);
      
      setIsAdmin(roles.isAdmin);
      setIsDealer(roles.isDealer);

      // If should be admin but role row not present, bootstrap and re-check once
      if (shouldBeAdmin && !roles.isAdmin) {
        const timeoutMs = 1500;
        await Promise.race([
          ensureAdminRole(sessionUser),
          new Promise((resolve) => setTimeout(resolve, timeoutMs)),
        ]);

        // Clear cache and re-check
        roleCheckCache.delete(sessionUser.id);
        roles = await fetchRoles(sessionUser.id, email);
        setIsAdmin(roles.isAdmin);
        setIsDealer(roles.isDealer);
      }
    } finally {
      setIsRoleLoading(false);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!initialSessionResolvedRef.current && event === 'INITIAL_SESSION' && !nextSession) {
        return;
      }

      setSession(nextSession);
      setUser(nextSession?.user ?? null);
       setIsEmailVerified(!!nextSession?.user?.email_confirmed_at);
      setIsLoading(false);

      if (nextSession?.user) {
        setTimeout(() => {
          void performRoleChecks(nextSession.user);
        }, 0);
      } else {
        setIsAdmin(false);
        setIsDealer(false);
         setIsEmailVerified(false);
        if (initialSessionResolvedRef.current) {
          setIsRoleLoading(false);
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      initialSessionResolvedRef.current = true;

      setSession(existingSession);
      setUser(existingSession?.user ?? null);
       setIsEmailVerified(!!existingSession?.user?.email_confirmed_at);
      setIsLoading(false);

      if (existingSession?.user) {
        setTimeout(() => {
          void performRoleChecks(existingSession.user);
        }, 0);
      } else {
        setIsAdmin(false);
        setIsDealer(false);
         setIsEmailVerified(false);
        setIsRoleLoading(false);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: redirectUrl,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = useCallback(async () => {
    // 1. Immediately reset all auth state to prevent stale renders
    const userId = user?.id;
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsDealer(false);
    setIsEmailVerified(false);
    setIsRoleLoading(false);

    // 2. Clear ALL client-side caches (not selective — nuke everything)
    queryClient.clear();

    // 3. Clear role check cache
    if (userId) {
      roleCheckCache.delete(userId);
    }

    // 4. Clear access cache
    import('@/hooks/useAccess').then(({ clearAccessCache }) => {
      if (userId) {
        clearAccessCache(userId);
      }
    });

    // 5. Sign out from Supabase (after state is already cleared)
    await supabase.auth.signOut();

    // 6. Navigate to home — use window.location for a clean slate
    //    (AuthProvider sits outside Router, so useNavigate isn't available)
    window.location.replace('/');
  }, [user?.id, queryClient]);

  const value = useMemo(
     () => ({ user, session, isAdmin, isDealer, isEmailVerified, isLoading, isRoleLoading, signIn, signUp, signOut }),
     [user, session, isAdmin, isDealer, isEmailVerified, isLoading, isRoleLoading, signOut]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
